import { captureException } from "@sentry/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { SENTRY_ENABLED } from "@/lib/sentry";
import { coreStore } from "@/lib/stores/core-store";
import { uiStore } from "@/lib/stores/ui-store";

type DocumentPictureInPicture = {
	requestWindow: (options?: {
		width?: number;
		height?: number;
		disallowReturnToOpener?: boolean;
	}) => Promise<Window>;
	window: Window | null;
};

function getDocumentPip(): DocumentPictureInPicture | undefined {
	return (
		window as unknown as { documentPictureInPicture?: DocumentPictureInPicture }
	).documentPictureInPicture;
}

function copyStylesheetsToPipWindow(pip: Window): void {
	const styleSheets = Array.from(document.styleSheets);
	for (const sheet of styleSheets) {
		try {
			if (sheet.href) {
				const link = pip.document.createElement("link");
				link.rel = "stylesheet";
				link.href = sheet.href;
				pip.document.head.appendChild(link);
			} else if (sheet.cssRules) {
				const style = pip.document.createElement("style");
				const cssText = Array.from(sheet.cssRules)
					.map((rule) => rule.cssText)
					.join("\n");
				style.textContent = cssText;
				pip.document.head.appendChild(style);
			}
		} catch (e) {
			console.warn("Could not copy stylesheet:", e);
			if (SENTRY_ENABLED)
				captureException(e as Error, {
					tags: { context: "pip_copy_stylesheet" },
				});
		}
	}
}

function setupPipWindowStyles(pip: Window): void {
	pip.document.body.style.margin = "0";
	pip.document.body.style.padding = "0";
	pip.document.body.style.overflow = "hidden";
	pip.document.body.classList.add("dark");
}

export type UseDocumentPipOptions = {
	width?: number;
	height?: number;
	onClose?: () => void;
};

export type UseDocumentPipReturn = {
	isSupported: boolean;
	isPipActive: boolean;
	pipWindow: Window | null;
	openPip: () => Promise<boolean>;
	closePip: () => void;
	pipContainerRef: React.RefObject<HTMLDivElement | null>;
};

export function useDocumentPip(
	options: UseDocumentPipOptions = {}
): UseDocumentPipReturn {
	const { width = 320, height = 240, onClose } = options;

	const [isPipActive, setIsPipActive] = useState(false);
	const [pipWindow, setPipWindow] = useState<Window | null>(null);
	const pipContainerRef = useRef<HTMLDivElement | null>(null);
	const pipWindowRef = useRef<Window | null>(null);
	const onCloseRef = useRef(onClose);
	const widthRef = useRef(width);
	const heightRef = useRef(height);

	onCloseRef.current = onClose;
	pipWindowRef.current = pipWindow;
	widthRef.current = width;
	heightRef.current = height;

	const isSupported =
		typeof window !== "undefined" && getDocumentPip() !== undefined;

	const closePip = useCallback(() => {
		if (pipWindowRef.current && !pipWindowRef.current.closed) {
			pipWindowRef.current.close();
		}
		setPipWindow(null);
		setIsPipActive(false);
	}, []);

	const openPip = useCallback(async (): Promise<boolean> => {
		const state = uiStore.getState();
		const broadcast = coreStore.getState().broadcast;
		const hasActiveCall = !!state.activeChannel;
		const hasActiveBroadcast =
			(!!state.activeBroadcastViewer || !!state.broadcastRoom) &&
			broadcast?.status === "live";
		if (!(hasActiveCall || hasActiveBroadcast)) {
			return false;
		}

		const docPip = getDocumentPip();
		if (!docPip) {
			toast.error("Picture-in-Picture is not supported in this browser");
			return false;
		}

		if (pipWindowRef.current && !pipWindowRef.current.closed) {
			pipWindowRef.current.focus();
			return true;
		}

		try {
			const pip = await docPip.requestWindow({
				width: widthRef.current,
				height: heightRef.current,
				disallowReturnToOpener: false,
			});

			copyStylesheetsToPipWindow(pip);
			setupPipWindowStyles(pip);

			pipWindowRef.current = pip;
			setPipWindow(pip);
			setIsPipActive(true);

			pip.addEventListener("pagehide", () => {
				pipWindowRef.current = null;
				setPipWindow(null);
				setIsPipActive(false);
				onCloseRef.current?.();
			});
			return true;
		} catch (error) {
			console.error("Failed to open Picture-in-Picture window:", error);
			pipWindowRef.current = null;
			setPipWindow(null);
			setIsPipActive(false);
			if (SENTRY_ENABLED)
				captureException(error, { tags: { context: "pip_open_window" } });
			return false;
		}
	}, []);

	useEffect(
		() => () => {
			if (pipWindow && !pipWindow.closed) {
				pipWindow.close();
			}
		},
		[pipWindow]
	);

	return {
		isSupported,
		isPipActive,
		pipWindow,
		openPip,
		closePip,
		pipContainerRef,
	};
}
